import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';

const INITIAL_STATE = {
  status: 'connecting',
  players: [],
  scores: [],
  currentRound: 0,
  totalRounds: 5,
  hostPlayerId: null,
  gameMode: null,
  gameState: null,
  // Live round data (cricketer + axes) — populated on guess_round_public
  round: null,
  // Round results (pins + correct) — populated on guess_results_public; cleared on next round
  results: null,
};

function pickSocketUrl() {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export function useBroadcastGameState(gameId) {
  const [state, setState] = useState(INITIAL_STATE);

  useEffect(() => {
    if (!gameId) {
      setState({ ...INITIAL_STATE, status: 'missing-gameid' });
      return undefined;
    }

    const socket = io(pickSocketUrl(), {
      transports: ['websocket', 'polling'],
      timeout: 5000,
    });

    const mergeGame = (game) => {
      if (!game) return;
      setState((prev) => ({
        ...prev,
        status: 'live',
        players: (game.players || []).map((p) => (typeof p === 'string' ? p : p.id)),
        scores: (game.players || []).map((p) =>
          typeof p === 'string' ? { id: p, score: 0 } : { id: p.id, score: p.score ?? 0 }
        ),
        currentRound: game.currentRound ?? prev.currentRound,
        gameMode: game.gameMode ?? prev.gameMode,
        gameState: game.gameState ?? prev.gameState,
      }));
    };

    socket.on('connect', () => {
      socket.emit('join_public_view', { gameId });
    });

    socket.on('public_view_joined', (data) => mergeGame(data.game));
    socket.on('game_state_update', (data) => mergeGame(data.game || data));
    socket.on('player_joined_public', (data) => mergeGame(data.game));

    socket.on('guess_round_public', (data) => {
      setState((prev) => ({
        ...prev,
        status: 'live',
        round: data,
        results: null,
        currentRound: data.currentRound ?? prev.currentRound,
        totalRounds: data.totalRounds ?? prev.totalRounds,
      }));
    });

    socket.on('guess_results_public', (data) => {
      setState((prev) => ({
        ...prev,
        status: 'live',
        results: data,
        scores: data.scores || prev.scores,
        currentRound: data.currentRound ?? prev.currentRound,
        totalRounds: data.totalRounds ?? prev.totalRounds,
      }));
    });

    socket.on('game_not_found', () => {
      setState((prev) => ({ ...prev, status: 'not-found' }));
    });

    socket.on('disconnect', () => {
      setState((prev) => ({ ...prev, status: 'disconnected' }));
    });

    return () => {
      socket.disconnect();
    };
  }, [gameId]);

  return state;
}
